import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HStack, VStack, Text, Input, NumberInput, NumberInputField, Select, Checkbox } from '@chakra-ui/react';
import { useAppStore } from '../../state/store';
import { useTextStyleStore } from '../../tools/cad/TextTools';
import { AppDialog, DialogButton } from '../common/AppDialog';
import { runInteraction, PT } from '../../core/commands/runners/lib';
import { undoableAdd, makeEntity } from '../../core/commands/entityOps';

/**
 * ATTDEF dialog (Faz 8.9, OCS attdef_dialog parity): tag/prompt/default +
 * the AFLAGS checkbox set (1 Invisible, 2 Constant, 4 Verify, 8 Preset,
 * 16 Lock position, 32 Multiple lines) + annotative + text settings +
 * "align below previous attribute" + the fx FIELD shortcut (Faz 8.8 dialog).
 *
 * Render semantics wired in EntityLayer/BlockRefMesh:
 *  - Invisible → hidden in model space, visible inside the Block Editor
 *  - Constant  → no prompt at insert time (value IS the default)
 */

/** AFLAGS bit values (DXF attdef semantics, OCS AttdefDialogState order). */
export const AFLAGS = {
    INVISIBLE: 1,
    CONSTANT: 2,
    VERIFY: 4,
    PRESET: 8,
    LOCK: 16,
    MULTILINE: 32,
} as const;

export interface AttdefSpec {
    tag: string;
    prompt: string;
    defaultValue: string;
    aflags: number;
    annotative: boolean;
    justify: string;
    styleId: string;
    height: number;
    rotation: number;
    alignBelow: boolean;
}

/** Session memory of the last placed attribute (OCS "align below previous"). */
let lastAttdefPosition: [number, number, number] | null = null;

export function AttdefDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'attdef');
    const onClose = useAppStore((state) => state.closeManageDialog);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const styles = useTextStyleStore((s) => s.styles);
    const [tag, setTag] = useState('');
    const [prompt, setPrompt] = useState('');
    const [defaultValue, setDefaultValue] = useState('');
    const [invisible, setInvisible] = useState(false);
    const [constant, setConstant] = useState(false);
    const [verify, setVerify] = useState(false);
    const [preset, setPreset] = useState(false);
    const [lockPosition, setLockPosition] = useState(true);
    const [multiline, setMultiline] = useState(false);
    const [annotative, setAnnotative] = useState(false);
    const [justify, setJustify] = useState('left');
    const [styleId, setStyleId] = useState('standard');
    const [height, setHeight] = useState(0.25);
    const [rotation, setRotation] = useState(0);
    const [alignBelow, setAlignBelow] = useState(false);
    const [error, setError] = useState('');

    const tagOk = tag.trim().length > 0 && !/\s/.test(tag.trim());
    const constantMode = constant;

    const insert = () => {
        if (!tagOk) { setError(t('ui.cad.attdef.tagRequired')); return; }
        onClose();
        const aflags =
            (invisible ? AFLAGS.INVISIBLE : 0) |
            (constant ? AFLAGS.CONSTANT : 0) |
            (verify ? AFLAGS.VERIFY : 0) |
            (preset ? AFLAGS.PRESET : 0) |
            (lockPosition ? AFLAGS.LOCK : 0) |
            (multiline ? AFLAGS.MULTILINE : 0);
        const spec: AttdefSpec = {
            tag: tag.trim(), prompt, defaultValue, aflags, annotative,
            justify, styleId, height, rotation, alignBelow,
        };
        const below = alignBelow && lastAttdefPosition;
        runInteraction({
            steps: [PT(below
                ? `ATTDEF "${spec.tag}": pick point (Enter aligns below the previous attribute)`
                : `ATTDEF "${spec.tag}": pick point`)],
            complete: (pts) => {
                const pos = below
                    ? [lastAttdefPosition![0], lastAttdefPosition![1] - height * 1.8, lastAttdefPosition![2] ?? 0]
                    : [pts[0].x, pts[0].y, pts[0].z];
                lastAttdefPosition = pos as [number, number, number];
                undoableAdd([makeEntity('attdef', `AttDef-${spec.tag}-${Date.now()}`, {
                    tag: spec.tag,
                    prompt: constantMode ? '' : spec.prompt,
                    defaultValue: spec.defaultValue,
                    aflags: spec.aflags,
                    annotative: spec.annotative,
                    justify: spec.justify,
                    styleId: spec.styleId,
                    height: spec.height,
                    rotation: spec.rotation,
                    position: pos,
                }, activeLayerId)]);
                return `ATTDEF: attribute "${spec.tag}" placed (aflags ${spec.aflags}).`;
            },
        });
    };

    return (
        <AppDialog
            isOpen={isOpen}
            onClose={onClose}
            title={t('ui.cad.attdef.title')}
            size="md"
            footer={
                <HStack justify="flex-end" w="full" spacing={2}>
                    <DialogButton variant="primary" onClick={insert} isDisabled={!tagOk}>
                        {t('ui.cad.attdef.insert')}
                    </DialogButton>
                    <DialogButton variant="ghost" onClick={onClose}>{t('common.cancel')}</DialogButton>
                </HStack>
            }
        >
            <VStack spacing={3} align="stretch">
                <HStack justify="space-between" spacing={3}>
                    <Text fontSize="sm" w="90px" flexShrink={0} color="var(--color-text-secondary)">{t('ui.cad.attdef.tag')}</Text>
                    <Input size="sm" value={tag} onChange={(e) => { setTag(e.target.value); setError(''); }}
                        bg="var(--color-bg-primary)" borderColor="var(--color-border)" />
                </HStack>
                <HStack justify="space-between" spacing={3}>
                    <Text fontSize="sm" w="90px" flexShrink={0} color="var(--color-text-secondary)">{t('ui.cad.attdef.prompt')}</Text>
                    <Input size="sm" value={prompt} isDisabled={constantMode}
                        onChange={(e) => setPrompt(e.target.value)}
                        bg="var(--color-bg-primary)" borderColor="var(--color-border)" />
                </HStack>
                <HStack justify="space-between" spacing={3}>
                    <Text fontSize="sm" w="90px" flexShrink={0} color="var(--color-text-secondary)">
                        {constantMode ? t('ui.cad.attdef.default') : t('ui.cad.attdef.default')}
                    </Text>
                    <Input size="sm" value={defaultValue} onChange={(e) => setDefaultValue(e.target.value)}
                        bg="var(--color-bg-primary)" borderColor="var(--color-border)" />
                </HStack>

                {/* AFLAGS mode checkboxes (OCS bit order). */}
                <VStack spacing={1} align="flex-start" pt={1}>
                    <Text fontSize="xs" color="var(--color-text-secondary)">{t('ui.cad.attdef.mode')}</Text>
                    <HStack spacing={4} flexWrap="wrap">
                        <Checkbox size="sm" isChecked={invisible} onChange={(e) => setInvisible(e.target.checked)}>
                            <Text fontSize="sm">{t('ui.cad.attdef.invisible')}</Text>
                        </Checkbox>
                        <Checkbox size="sm" isChecked={constant} onChange={(e) => setConstant(e.target.checked)}>
                            <Text fontSize="sm">{t('ui.cad.attdef.constant')}</Text>
                        </Checkbox>
                        <Checkbox size="sm" isChecked={verify} onChange={(e) => setVerify(e.target.checked)}>
                            <Text fontSize="sm">{t('ui.cad.attdef.verify')}</Text>
                        </Checkbox>
                        <Checkbox size="sm" isChecked={preset} onChange={(e) => setPreset(e.target.checked)}>
                            <Text fontSize="sm">{t('ui.cad.attdef.preset')}</Text>
                        </Checkbox>
                    </HStack>
                    <HStack spacing={4} flexWrap="wrap">
                        <Checkbox size="sm" isChecked={lockPosition} onChange={(e) => setLockPosition(e.target.checked)}>
                            <Text fontSize="sm">{t('ui.cad.attdef.lockPosition')}</Text>
                        </Checkbox>
                        <Checkbox size="sm" isChecked={multiline} onChange={(e) => setMultiline(e.target.checked)}>
                            <Text fontSize="sm">{t('ui.cad.attdef.multiline')}</Text>
                        </Checkbox>
                        <Checkbox size="sm" isChecked={annotative} onChange={(e) => setAnnotative(e.target.checked)}>
                            <Text fontSize="sm">{t('ui.cad.attdef.annotative')}</Text>
                        </Checkbox>
                    </HStack>
                </VStack>

                {/* Text settings. */}
                <VStack spacing={1} align="flex-start">
                    <Text fontSize="xs" color="var(--color-text-secondary)">{t('ui.cad.attdef.text')}</Text>
                    <HStack spacing={3} flexWrap="wrap">
                        <Select size="sm" w="120px" value={justify} onChange={(e) => setJustify(e.target.value)}>
                            <option value="left">Left</option>
                            <option value="center">Center</option>
                            <option value="right">Right</option>
                        </Select>
                        <Select size="sm" w="140px" value={styleId} onChange={(e) => setStyleId(e.target.value)}>
                            {Object.values(styles).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </Select>
                        <NumberInput size="sm" w="100px" value={height} min={0.05} step={0.05}
                            onChange={(_, v) => setHeight(Number.isFinite(v) && v > 0 ? v : height)}>
                            <NumberInputField bg="var(--color-bg-primary)" borderColor="var(--color-border)" />
                        </NumberInput>
                        <NumberInput size="sm" w="110px" value={rotation} step={15}
                            onChange={(_, v) => setRotation(Number.isFinite(v) ? v : rotation)}>
                            <NumberInputField bg="var(--color-bg-primary)" borderColor="var(--color-border)" />
                        </NumberInput>
                    </HStack>
                </VStack>

                <Checkbox size="sm" isChecked={alignBelow} onChange={(e) => setAlignBelow(e.target.checked)}
                    isDisabled={!lastAttdefPosition}>
                    <Text fontSize="sm">{t('ui.cad.attdef.alignBelow')}</Text>
                </Checkbox>
                {error && <Text fontSize="xs" color="red.400">{error}</Text>}
            </VStack>
        </AppDialog>
    );
}
